import { Link } from "react-router-dom";
import { ArrowRight, Handshake, Package, Repeat, TrendingUp, Users } from "lucide-react";
import { Reveal } from "../Reveal";
import IconTile from "../IconTile";

const EARN_STEPS = [
  {
    icon: Users,
    title: "Get clients",
    desc: "Sign local businesses - restaurants, salons, clinics, real estate, retailers, gyms, hotels and professionals all need this.",
  },
  {
    icon: Package,
    title: "Sell solutions",
    desc: "Package AI automation, QR software and business tools into your own three tiers of plans, priced your way.",
  },
  {
    icon: Repeat,
    title: "Charge every month",
    desc: "Bill each client a recurring subscription for the platform and the work - predictable MRR, not one-off projects.",
  },
  {
    icon: Handshake,
    title: "Upsell services",
    desc: "Layer on setup fees, content, ads management and extra products, account by account.",
  },
  {
    icon: TrendingUp,
    title: "Scale the margin",
    desc: "Your platform cost stays flat while every new client compounds revenue. We stay invisible; you keep the relationship.",
  },
];

export default function HowAgenciesEarn() {
  return (
    <section
      id="how-agencies-earn"
      data-testid="how-agencies-earn-section"
      className="relative py-24 md:py-32 border-t border-[#E6E9F1] overflow-hidden bg-[#F4F6FB]"
    >
      <div className="mb-blob mb-blob-orange mb-anim-a w-[360px] h-[360px] -top-24 -left-24" />
      <div className="absolute inset-0 mb-grid-lines opacity-60" />
      <div className="relative max-w-7xl mx-auto px-6 md:px-12 lg:px-8">
        <div className="grid gap-14 lg:grid-cols-2 lg:items-center">
          {/* Copy */}
          <Reveal>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold tracking-tighter leading-none text-[#0B1533]">
              How your agency{" "}
              <span className="text-[#FF6B00]">actually earns</span>
            </h2>
            <p className="mt-5 text-base md:text-lg text-[#475569] leading-relaxed">
              Agency OS is a product line you sell, not a tool you rent. Buy the platform
              once, resell it to every local business you win, and keep the margin.
            </p>

            <ol className="mt-8 space-y-3">
              {EARN_STEPS.map((s, i) => (
                <li
                  key={s.title}
                  className="group flex gap-4 rounded-2xl border border-[#E6E9F1] bg-white p-4 mb-card-shadow transition-all duration-300 hover:border-[#FF6B00]/30 hover:shadow-[0_12px_28px_rgba(255,107,0,0.10)]"
                >
                  <IconTile icon={s.icon} size="sm" />
                  <div className="min-w-0">
                    <h3 className="flex items-center gap-2 text-base font-bold text-[#0B1533]">
                      {s.title}
                      <span className="text-xs font-semibold text-[#94A3B8]">Step {i + 1}</span>
                    </h3>
                    <p className="mt-1 text-sm text-[#475569] leading-relaxed">{s.desc}</p>
                  </div>
                </li>
              ))}
            </ol>

            <Link
              to="/how-agencies-earn"
              data-testid="explore-earnings-link"
              className="mt-8 inline-flex items-center gap-2 text-sm font-semibold text-[#FF6B00] hover:gap-3 transition-all"
            >
              See the full earnings breakdown
              <ArrowRight className="w-4 h-4" />
            </Link>
          </Reveal>

          {/* Visual */}
          <Reveal delay={0.15}>
            <div className="relative mx-auto max-w-[560px]">
              <div
                aria-hidden
                className="pointer-events-none absolute -inset-8 rounded-[48px] bg-[#16A34A] blur-3xl opacity-[0.12]"
              />
              <img
                src="/how-agency-earn-magicbolt.webp"
                alt="How agencies earn recurring revenue reselling Agency OS"
                width="1260"
                height="1260"
                loading="lazy"
                decoding="async"
                className="relative block w-full h-auto object-contain drop-shadow-[0_28px_50px_rgba(11,21,51,0.16)]"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
